import { cn } from '@/lib/utils';
import { RequestSelectionItem } from './RequestSelectionItem';

interface RequestMethodsGroupProps {
  title: string;
  methods: string[];
  selectedMethods: string[];
  onToggle: (method: string) => void;
  isDisabled: (method: string) => boolean;
  showError?: boolean;
  errorMessage?: string;
  methodErrors?: Record<string, { message: string; grouped?: boolean }>;
  className?: string;
}

export const RequestMethodsGroup = ({
  title,
  methods,
  selectedMethods,
  onToggle,
  isDisabled,
  showError,
  errorMessage,
  methodErrors,
  className,
}: RequestMethodsGroupProps) => {
  if (methods.length === 0) return null;
  const groupedErrorMethods = methods.filter(
    (method) => methodErrors?.[method]?.grouped,
  );
  const hasGroupedRange = groupedErrorMethods.length > 1;
  const firstGroupedIndex = methods.findIndex((method) =>
    groupedErrorMethods.includes(method),
  );
  let lastGroupedIndex = -1;
  if (hasGroupedRange) {
    for (let index = methods.length - 1; index >= 0; index -= 1) {
      if (groupedErrorMethods.includes(methods[index])) {
        lastGroupedIndex = index;
        break;
      }
    }
  }

  const renderMethod = (method: string, showMethodError: boolean) => (
    <RequestSelectionItem
      key={method}
      method={method}
      selected={selectedMethods.includes(method)}
      disabled={isDisabled(method)}
      onToggle={onToggle}
      showError={showMethodError}
    />
  );

  if (!hasGroupedRange) {
    return (
      <div className={cn(className)}>
        <div className="mb-4 text-xl">{title}</div>
        <div className="flex flex-col gap-2">
          {methods.map((method) =>
            renderMethod(method, Boolean(methodErrors?.[method])),
          )}
        </div>
        {showError ? (
          <div className="text-destructive mt-2 text-sm">
            {errorMessage ?? 'Please fix validation errors.'}
          </div>
        ) : null}
      </div>
    );
  }

  // Keep the original section order while wrapping only the shared error range.
  const methodsBeforeGrouped = methods.slice(0, firstGroupedIndex);
  const groupedMethods = methods.slice(firstGroupedIndex, lastGroupedIndex + 1);
  const methodsAfterGrouped = methods.slice(lastGroupedIndex + 1);

  return (
    <div className={cn(className)}>
      <div className="mb-4 text-xl">{title}</div>
      <div className="flex flex-col gap-2">
        {methodsBeforeGrouped.map((method) =>
          renderMethod(method, Boolean(methodErrors?.[method])),
        )}

        {groupedMethods.length > 0 ? (
          <div className="border-destructive rounded-md border-2 p-2">
            <div className="flex flex-col gap-2">
              {groupedMethods.map((method) => renderMethod(method, false))}
            </div>
          </div>
        ) : null}

        {methodsAfterGrouped.map((method) =>
          renderMethod(method, Boolean(methodErrors?.[method])),
        )}
      </div>
      {showError ? (
        <div className="text-destructive mt-2 text-sm">
          {errorMessage ?? 'Please fix validation errors.'}
        </div>
      ) : null}
    </div>
  );
};
